import {useSyncExternalStore} from 'react';
import {getAffidavitJurisdictionRule} from './affidavitJurisdictionRules';
import {holmesThinkingStore, trackedByHolmes} from './holmesThinkingStore';

/** Simulated round-trip latency so the Holmes Jr. thinking indicator is perceptible, not instant. */
const THINKING_DELAY_MS = 350;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function mutate<T>(work: () => T): Promise<T> {
  return trackedByHolmes(
    (async () => {
      await delay(THINKING_DELAY_MS);
      return work();
    })(),
  );
}

/**
 * Exception-state pass, 2026-09-11: added `address_not_found` and
 * `access_blocked` -- previously every unsuccessful attempt fell into one
 * generic `not_served` bucket, which lost the actual reason (a server who
 * showed up to a bad address has a very different next step than one who
 * was turned away at a gate). This also closes most of a vocabulary gap
 * already flagged in docs/ai/CLAUDE_UI_BACKEND_SEAM_MAP.md: the real
 * `recordServiceAttempt` outcome codes are `served_recipient |
 * served_substitute | no_contact | address_not_found | access_blocked`.
 * `served_entity` has no real equivalent (a deliberate Claude addition for
 * corporate/registered-agent service, a real and common case this UI
 * needed) and `not_served` is kept as an honest catch-all for a reason that
 * doesn't fit the specific categories -- reconciling those two fully is
 * still a decision for Codex/Russell, not resolved here.
 */
export type ResultPath = 'served_personal' | 'served_substitute' | 'served_entity' | 'address_not_found' | 'access_blocked' | 'not_served';
export type ReviewState = 'not_submitted' | 'pending_review' | 'accepted' | 'returned_for_correction';
export type PackageState = 'awaiting_acknowledgment' | 'acknowledged';
export type Urgency = 'on_track' | 'due_today' | 'overdue';
export type AffidavitState = 'not_started' | 'server_preparing' | 'company_review' | 'accepted';
export type AttestationPath = 'notary' | 'self_authenticating_mail';
export type ClosureState = 'open' | 'complete';
export type FinancialState = 'open' | 'closed';

export const SERVED_RESULT_PATHS: ResultPath[] = ['served_personal', 'served_substitute', 'served_entity'];

export function wasServed(result: ResultPath): boolean {
  return SERVED_RESULT_PATHS.includes(result);
}

export const RESULT_LABEL: Record<ResultPath, string> = {
  served_personal: 'Personal service',
  served_substitute: 'Substitute service',
  served_entity: 'Entity / authorized-recipient service',
  address_not_found: 'Not served — address not found',
  access_blocked: 'Not served — access blocked (gated, refused entry, etc.)',
  not_served: 'Not served — other reason',
};

/**
 * Thrown when a mutation's `expectedVersion` no longer matches the job's
 * current version — the same optimistic-concurrency shape api.ts's real
 * mutations already use (see docs/ai/CLAUDE_MODULE_UI_FIXTURE_CONTRACT.md).
 */
export class WorkflowConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkflowConflictError';
  }
}

export type AttemptHistoryEntry = {
  result: ResultPath;
  narrative: string;
  evidenceFileName?: string;
  correctionNote: string;
};

export type Attempt = {
  result: ResultPath;
  attemptedAt: string;
  narrative: string;
  evidenceFileName?: string;
  evidenceDataUrl?: string;
  reviewState: ReviewState;
  correctionNote?: string;
  /** Prior submissions this attempt superseded, oldest first, each paired with why it was returned. */
  history?: AttemptHistoryEntry[];
};

export const KNOWN_SERVERS = ['Unassigned', 'Dana Whitfield', 'Priya Nakamura'];

/**
 * Document handling fee rule (Russell, 2026-09-10): the free allowance and
 * per-page rate apply per printed copy, i.e. per Service Job -- one Service
 * Job already represents one recipient's copy of the packet, so this maps
 * directly onto the existing per-recipient job model with no restructuring.
 * This is a displayed/recorded fee estimate only; nothing here charges or
 * processes payment (matches the existing "record external handling, never
 * process payment" doctrine already used elsewhere in this store).
 */
export const PAGE_COUNT_FREE_ALLOWANCE = 30;
export const PAGE_OVERAGE_RATE_USD = 0.25;

export function documentHandlingFee(pageCount: number | undefined): number {
  if (!pageCount || pageCount <= PAGE_COUNT_FREE_ALLOWANCE) return 0;
  return (pageCount - PAGE_COUNT_FREE_ALLOWANCE) * PAGE_OVERAGE_RATE_USD;
}

/**
 * Witness fee rule (Russell, 2026-09-10): a subpoena may require a witness
 * fee to accompany service in certain jurisdictions. When ProFenz handles
 * that fee, it charges the witness fee amount itself plus a flat $10
 * administrative fee (covers check-cutting, service, and handling). Modeled
 * per Service Job, same as the document handling fee above, since the
 * witness fee is paid to a specific witness being served in that job.
 */
export const WITNESS_FEE_ADMIN_SURCHARGE_USD = 10;

export function witnessFeeTotal(witnessFeeAmount: number | undefined): number {
  if (!witnessFeeAmount || witnessFeeAmount <= 0) return 0;
  return witnessFeeAmount + WITNESS_FEE_ADMIN_SURCHARGE_USD;
}

export type ServiceJob = {
  id: string;
  fileId: string;
  subject: string;
  serviceAddress: string;
  serviceLevel: string;
  deadlineLabel: string;
  deadlineSortKey: string;
  urgency: Urgency;
  packageState: PackageState;
  server: string;
  episodeId: string;
  affidavitState: AffidavitState;
  affidavitNarrative?: string;
  affidavitPath?: AttestationPath;
  affidavitNotaryName?: string;
  /** Only meaningful where the job's jurisdiction requires it -- see affidavitJurisdictionRules.ts's militaryStatusRequired. */
  affidavitMilitaryStatusStatement?: string;
  affidavitCorrectionNote?: string;
  operationalClosure: ClosureState;
  financialClosure: FinancialState;
  /** Pages in this recipient's printed copy. The free-30/then-$0.25 document handling fee applies per Service Job (per copy), not per Company File. */
  pageCount?: number;
  /** Statutory witness fee amount, when this job is serving a subpoena that requires one accompany service. See witnessFeeTotal() for the +$10 admin surcharge. */
  witnessFeeAmount?: number;
  attempt: Attempt | null;
  /** Optimistic-concurrency version. Every mutation below requires the caller's `expectedVersion` to match. */
  version: number;
  /** Append-only record of what happened to this job, oldest first. Powers the completion/history/search view. */
  timeline: TimelineEvent[];
};

export type TimelineEvent = {
  label: string;
  detail?: string;
  at: string;
};

/**
 * Document status vocabulary matches the typed state machine Codex's PR #112
 * built for CustomerDocumentUploadAdapter.ts (see docs/ai/CLAUDE_UI_RETIREMENT_CANDIDATES.md,
 * "adopt the contract shape, not the implementation") rather than inventing a
 * fourth vocabulary -- 'pending_scan' and 'quarantined' are real states a
 * document can be in server-side, not synthetic inventions.
 */
export type DocumentStatus = 'accepted' | 'pending_scan' | 'quarantined' | 'duplicate' | 'too_large' | 'unavailable';

export type CompanyFileDocument = {
  id: string;
  fileName: string;
  category: 'Summons and complaint' | 'Subpoena' | 'Notice' | 'Field sheet' | 'Other';
  status: DocumentStatus;
  sizeKb: number;
  uploadedAt: string;
  /** Only set when status is 'accepted' and a later upload replaced this one -- the record itself is never removed, per the append-only evidence policy. */
  supersededBy?: string;
};

export type CompanyFile = {
  id: string;
  caption: string;
  caseNumber: string;
  court: string;
  /** 2-letter state code, used to look up affidavit requirements (affidavitJurisdictionRules.ts). 'OTHER' when the case's jurisdiction isn't one ProFenz has ported rules for yet -- affidavit review still proceeds, just without jurisdiction-specific prompts. */
  stateCode: string;
  originatingCustomer?: string;
  /**
   * Set only when this file arrived via cross-company platform routing --
   * i.e. another process-serving company's customer relationship, routed to
   * this company to fulfill because this company is better positioned
   * (different county/state, capacity, etc). That other company is the one
   * with the actual customer relationship, so the affidavit that reaches
   * the end customer should carry their name, not the fulfilling company's
   * -- the fulfilling server's own attestation/signature stays accurate
   * regardless, only the letterhead branding changes. Absent for a file a
   * company took on directly from its own customer.
   */
  sendingCompanyName?: string;
  sourceOrderReference?: string;
  jobIds: string[];
  documents?: CompanyFileDocument[];
};

/**
 * A customer/attorney request that has been submitted but not yet routed to
 * a specific servicing company -- the synthetic analog of the real system's
 * platform-level intake/custody stage (api.ts's `loadIntake` ->
 * `claimMissionControlCustody` -> `createFulfillmentRoute`, surfaced today
 * in `MissionControlApp.tsx`). Deliberately a separate holding area from
 * `CompanyFile`: nothing here belongs to any company's Mission Control until
 * `routePlatformIntakeToCompany` converts it. See the "two intake patterns"
 * note in docs/ai/CLAUDE_UI_BACKEND_SEAM_MAP.md.
 */
export type PlatformIntakeRequest = {
  id: string;
  caption: string;
  caseNumber: string;
  court: string;
  stateCode: string;
  originatingCustomer?: string;
  /** See CompanyFile.sendingCompanyName -- carried through routing so the receiving company's affidavit can be branded correctly. */
  sendingCompanyName?: string;
  sourceOrderReference?: string;
  jobs: NewJobInput[];
  submittedAt: string;
  version: number;
};

export type WorkflowState = {
  files: CompanyFile[];
  jobs: Record<string, ServiceJob>;
  platformIntakeRequests: PlatformIntakeRequest[];
};

function initialState(): WorkflowState {
  const jobs: ServiceJob[] = [
    {
      id: 'CF-2026-00088-S01',
      fileId: 'CF-2026-00088',
      subject: 'Morgan Ellery (individual defendant)',
      serviceAddress: '418 Cedarview Rd, Charlotte, NC 28205',
      serviceLevel: 'Rush',
      deadlineLabel: 'Last day to serve: today, 5:00 PM ET',
      deadlineSortKey: '2026-09-10T17:00',
      urgency: 'due_today',
      packageState: 'acknowledged',
      server: 'Dana Whitfield',
      episodeId: 'Assignment episode 1',
      affidavitState: 'not_started',
      operationalClosure: 'open',
      financialClosure: 'open',
      version: 1,
      pageCount: 40,
      timeline: [
        {label: 'Company file created', at: 'Sept 8, 2026 · 8:05 AM ET'},
        {label: 'Assigned to Dana Whitfield', at: 'Sept 8, 2026 · 8:06 AM ET'},
        {label: 'Package acknowledged by server', at: 'Sept 8, 2026 · 11:20 AM ET'},
        {label: 'Attempt submitted', detail: 'Documents left with a co-resident adult at the confirmed address who identified themselves as the subject’s spouse.', at: 'Sept 9, 2026 · 9:42 AM ET'},
      ],
      attempt: {
        result: 'served_substitute',
        attemptedAt: 'Sept 9, 2026 · 9:42 AM ET',
        narrative: 'Documents left with a co-resident adult at the confirmed address who identified themselves as the subject’s spouse.',
        evidenceFileName: 'porch-handoff.jpg',
        reviewState: 'pending_review',
      },
    },
    {
      id: 'CF-2026-00088-S02',
      fileId: 'CF-2026-00088',
      subject: 'Dunmore Holdings LLC (registered agent)',
      serviceAddress: '900 Corporate Way, Suite 400, Raleigh, NC 27601',
      serviceLevel: 'Standard',
      deadlineLabel: 'Last day to serve: Sept 18, 2026',
      deadlineSortKey: '2026-09-18T17:00',
      urgency: 'on_track',
      packageState: 'acknowledged',
      server: 'Priya Nakamura',
      episodeId: 'Assignment episode 1',
      affidavitState: 'accepted',
      affidavitNarrative:
        'I, Priya Nakamura, being duly sworn, state that on September 3, 2026 at 11:05 AM ET I served the within documents on Dunmore Holdings LLC by delivering them to its registered agent desk at 900 Corporate Way, Suite 400, Raleigh, NC 27601, an authorized recipient for service on the entity.',
      affidavitPath: 'notary',
      affidavitNotaryName: 'Consuela Ortiz, Notary Public, Wake County, NC',
      operationalClosure: 'complete',
      financialClosure: 'closed',
      version: 1,
      pageCount: 40,
      timeline: [
        {label: 'Company file created', at: 'Aug 29, 2026 · 9:00 AM ET'},
        {label: 'Assigned to Priya Nakamura', at: 'Aug 29, 2026 · 9:05 AM ET'},
        {label: 'Package acknowledged by server', at: 'Sept 2, 2026 · 3:40 PM ET'},
        {label: 'Attempt submitted', detail: 'Registered agent desk accepted documents on behalf of the entity.', at: 'Sept 3, 2026 · 11:05 AM ET'},
        {label: 'Attempt accepted', at: 'Sept 3, 2026 · 4:12 PM ET'},
        {label: 'Affidavit preparation started', at: 'Sept 3, 2026 · 4:20 PM ET'},
        {label: 'Affidavit submitted for company review', at: 'Sept 4, 2026 · 9:10 AM ET'},
        {label: 'Affidavit accepted', at: 'Sept 4, 2026 · 2:00 PM ET'},
        {label: 'Marked operationally complete', at: 'Sept 4, 2026 · 2:05 PM ET'},
      ],
      attempt: {
        result: 'served_entity',
        attemptedAt: 'Sept 3, 2026 · 11:05 AM ET',
        narrative: 'Registered agent desk accepted documents on behalf of the entity.',
        evidenceFileName: 'agent-desk.jpg',
        reviewState: 'accepted',
      },
    },
    {
      id: 'CF-2026-00081-S02',
      fileId: 'CF-2026-00081',
      subject: 'Harlow Freight Co. (registered agent)',
      serviceAddress: '2200 Freight Yard Rd, Durham, NC 27703',
      serviceLevel: 'Standard',
      deadlineLabel: 'Last day to serve: Sept 16, 2026',
      deadlineSortKey: '2026-09-16T17:00',
      urgency: 'on_track',
      packageState: 'acknowledged',
      server: 'Dana Whitfield',
      episodeId: 'Assignment episode 1',
      affidavitState: 'not_started',
      operationalClosure: 'open',
      financialClosure: 'open',
      version: 1,
      timeline: [
        {label: 'Company file created', at: 'Sept 5, 2026 · 10:00 AM ET'},
        {label: 'Assigned to Dana Whitfield', at: 'Sept 5, 2026 · 10:02 AM ET'},
        {label: 'Package acknowledged by server', at: 'Sept 7, 2026 · 1:15 PM ET'},
        {label: 'Attempt submitted', detail: 'Front-desk clerk accepted on behalf of the company.', at: 'Sept 8, 2026 · 2:15 PM ET'},
        {
          label: 'Attempt returned for correction',
          detail: 'The clerk you named isn’t listed as a registered agent or officer — confirm the exact name/title of whoever accepted, or return to serve the actual registered agent.',
          at: 'Sept 8, 2026 · 5:30 PM ET',
        },
      ],
      attempt: {
        result: 'served_entity',
        attemptedAt: 'Sept 8, 2026 · 2:15 PM ET',
        narrative: 'Front-desk clerk accepted on behalf of the company.',
        evidenceFileName: 'front-desk.jpg',
        reviewState: 'returned_for_correction',
        correctionNote:
          'The clerk you named isn’t listed as a registered agent or officer — confirm the exact name/title of whoever accepted, or return to serve the actual registered agent.',
      },
    },
    {
      id: 'CF-2026-00095-S01',
      fileId: 'CF-2026-00095',
      subject: 'Marsh Fabrication LLC (registered agent)',
      serviceAddress: '4410 Industrial Loop, Raleigh, NC 27604',
      serviceLevel: 'Standard',
      deadlineLabel: 'Last day to serve: Sept 20, 2026',
      deadlineSortKey: '2026-09-20T17:00',
      urgency: 'on_track',
      packageState: 'awaiting_acknowledgment',
      server: 'Dana Whitfield',
      episodeId: 'Assignment episode 1',
      affidavitState: 'not_started',
      operationalClosure: 'open',
      financialClosure: 'open',
      version: 1,
      timeline: [
        {label: 'Company file created', at: 'Sept 10, 2026 · 9:00 AM ET'},
        {label: 'Assigned to Dana Whitfield', at: 'Sept 10, 2026 · 9:01 AM ET'},
      ],
      attempt: null,
    },
    {
      id: 'CF-2026-00093-S01',
      fileId: 'CF-2026-00093',
      subject: 'Devon Castillo (witness)',
      serviceAddress: '77 Harbor Bluff Ct, Durham, NC 27701',
      serviceLevel: 'Standard',
      deadlineLabel: 'Last day to serve: Sept 15, 2026',
      deadlineSortKey: '2026-09-15T17:00',
      urgency: 'on_track',
      packageState: 'awaiting_acknowledgment',
      server: 'Unassigned',
      episodeId: '',
      affidavitState: 'not_started',
      operationalClosure: 'open',
      financialClosure: 'open',
      version: 1,
      timeline: [{label: 'Company file created', at: 'Sept 9, 2026 · 4:30 PM ET'}],
      attempt: null,
    },
  ];

  const files: CompanyFile[] = [
    {
      id: 'CF-2026-00088',
      caption: 'Whitfield v. Dunmore Holdings LLC',
      caseNumber: '26-CVS-004417',
      court: 'Mecklenburg County Superior Court, NC',
      stateCode: 'NC',
      originatingCustomer: 'Ashgrove & Pine LLP — synthetic',
      sourceOrderReference: 'Order ref AP-2026-0071 (optional link, not tenant ownership)',
      jobIds: ['CF-2026-00088-S01', 'CF-2026-00088-S02'],
      documents: [
        {id: 'DOC-1', fileName: 'summons-complaint-signed.pdf', category: 'Summons and complaint', status: 'accepted', sizeKb: 1840, uploadedAt: 'Sept 8, 2026 · 8:02 AM ET', supersededBy: 'DOC-2'},
        {id: 'DOC-2', fileName: 'summons-complaint-signed-v2.pdf', category: 'Summons and complaint', status: 'accepted', sizeKb: 1852, uploadedAt: 'Sept 8, 2026 · 8:06 AM ET'},
        {id: 'DOC-3', fileName: 'field-sheet.pdf', category: 'Field sheet', status: 'pending_scan', sizeKb: 210, uploadedAt: 'Sept 8, 2026 · 8:07 AM ET'},
        {id: 'DOC-4', fileName: 'summons-complaint-signed.pdf', category: 'Summons and complaint', status: 'duplicate', sizeKb: 1840, uploadedAt: 'Sept 8, 2026 · 8:09 AM ET'},
        {id: 'DOC-5', fileName: 'exhibit-bundle.zip', category: 'Other', status: 'quarantined', sizeKb: 4096, uploadedAt: 'Sept 8, 2026 · 8:11 AM ET'},
      ],
    },
    {
      id: 'CF-2026-00081',
      caption: 'Nguyen v. Harlow Freight Co.',
      caseNumber: '26-CVS-003960',
      court: 'Durham County Superior Court, NC',
      stateCode: 'NC',
      originatingCustomer: 'Voss Litigation Group — synthetic',
      sendingCompanyName: 'Piedmont Legal Support Services — synthetic',
      jobIds: ['CF-2026-00081-S02'],
      documents: [
        {id: 'DOC-6', fileName: 'complaint.pdf', category: 'Summons and complaint', status: 'accepted', sizeKb: 980, uploadedAt: 'Aug 28, 2026 · 3:10 PM ET'},
        {id: 'DOC-7', fileName: 'deposition-video.mp4', category: 'Other', status: 'too_large', sizeKb: 512000, uploadedAt: 'Aug 28, 2026 · 3:12 PM ET'},
        {id: 'DOC-8', fileName: 'notice-of-hearing.pdf', category: 'Notice', status: 'unavailable', sizeKb: 340, uploadedAt: 'Aug 28, 2026 · 3:15 PM ET'},
      ],
    },
    {
      id: 'CF-2026-00095',
      caption: 'Castellini v. Marsh Fabrication LLC',
      caseNumber: '26-CVS-004488',
      court: 'Wake County Superior Court, NC',
      stateCode: 'NC',
      originatingCustomer: 'Ashgrove & Pine LLP — synthetic',
      jobIds: ['CF-2026-00095-S01'],
    },
    {
      id: 'CF-2026-00093',
      caption: 'In re Subpoena — Castillo Medical Records',
      caseNumber: '26-CVD-005112',
      court: 'Wake County District Court, NC',
      stateCode: 'NC',
      originatingCustomer: 'Voss Litigation Group — synthetic',
      jobIds: ['CF-2026-00093-S01'],
    },
  ];

  return {files, jobs: Object.fromEntries(jobs.map((job) => [job.id, job])), platformIntakeRequests: []};
}

let state: WorkflowState = initialState();
const listeners = new Set<() => void>();
/** Idempotency keys seen so far, purely for call-site parity with the real API — no dedup logic runs on them here. */
const seenIdempotencyKeys = new Set<string>();

function emit() {
  for (const listener of listeners) listener();
}

function requireJob(jobId: string): ServiceJob {
  const job = state.jobs[jobId];
  if (!job) throw new Error(`Unknown service job: ${jobId}`);
  return job;
}

function fileForJob(job: ServiceJob): CompanyFile | undefined {
  return state.files.find((file) => file.id === job.fileId);
}

/** Best-effort only -- looks for a trailing ", XX" 2-letter state abbreviation in free-text court strings like "Wake County Superior Court, NC". Falls back to 'OTHER' (no jurisdiction guidance shown) rather than guessing wrong. */
export function deriveStateCodeFromCourtText(courtText: string): string {
  const match = courtText.trim().match(/,\s*([A-Za-z]{2})$/);
  return match ? match[1].toUpperCase() : 'OTHER';
}

function setJob(jobId: string, expectedVersion: number, idempotencyKey: string, update: Partial<ServiceJob>, event?: {label: string; detail?: string}): ServiceJob {
  const current = requireJob(jobId);
  if (current.version !== expectedVersion) {
    throw new WorkflowConflictError(`${jobId} changed since it was loaded (expected version ${expectedVersion}, currently ${current.version}). Reload before retrying.`);
  }
  seenIdempotencyKeys.add(idempotencyKey);
  const timeline = event ? [...current.timeline, {label: event.label, detail: event.detail, at: 'Just now (synthetic)'}] : current.timeline;
  const next: ServiceJob = {...current, ...update, timeline, version: current.version + 1};
  state = {...state, jobs: {...state.jobs, [jobId]: next}};
  emit();
  return next;
}

export type NewJobInput = {
  documentsToServe?: string[];
  subject: string;
  serviceAddress: string;
  serviceLevel: 'Rush' | 'Standard';
  deadlineDate: string;
  server: string;
  pageCount?: number;
  witnessFeeAmount?: number;
};

export type NewCompanyFileInput = {
  plaintiff?: string;
  defendant?: string;
  county?: string;
  caption: string;
  caseNumber: string;
  court: string;
  stateCode: string;
  originatingCustomer?: string;
  sendingCompanyName?: string;
  sourceOrderReference?: string;
  jobs: NewJobInput[];
};

function formatDeadlineLabel(dateStr: string, isRush: boolean): string {
  const date = new Date(`${dateStr}T17:00:00`);
  const formatted = Number.isNaN(date.getTime())
    ? dateStr
    : date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
  return isRush ? `Last day to serve: ${formatted}, 5:00 PM ET` : `Last day to serve: ${formatted}`;
}

let sequence = 100;

function createCompanyFileSync(input: NewCompanyFileInput, idempotencyKey: string): string {
  seenIdempotencyKeys.add(idempotencyKey);
  sequence += 1;
  const fileId = `CF-2026-${String(sequence).padStart(5, '0')}`;
  const newJobs: ServiceJob[] = input.jobs.map((jobInput, index) => {
    const isRush = jobInput.serviceLevel === 'Rush';
    return {
      id: `${fileId}-S${String(index + 1).padStart(2, '0')}`,
      fileId,
      subject: jobInput.subject,
      serviceAddress: jobInput.serviceAddress,
      serviceLevel: jobInput.serviceLevel,
      deadlineLabel: formatDeadlineLabel(jobInput.deadlineDate, isRush),
      deadlineSortKey: `${jobInput.deadlineDate}T${isRush ? '17:00' : '23:59'}`,
      urgency: isRush ? 'due_today' : 'on_track',
      packageState: 'awaiting_acknowledgment',
      server: jobInput.server || 'Unassigned',
      episodeId: jobInput.server && jobInput.server !== 'Unassigned' ? 'Assignment episode 1' : '',
      affidavitState: 'not_started',
      operationalClosure: 'open',
      financialClosure: 'open',
      version: 1,
      pageCount: jobInput.pageCount,
      witnessFeeAmount: jobInput.witnessFeeAmount,
      timeline: [
        {label: 'Company file created', at: 'Just now (synthetic)'},
        ...(jobInput.server && jobInput.server !== 'Unassigned' ? [{label: `Assigned to ${jobInput.server}`, at: 'Just now (synthetic)'}] : []),
        ...(jobInput.pageCount
          ? [
              {
                label: `Page count recorded: ${jobInput.pageCount}`,
                detail: documentHandlingFee(jobInput.pageCount) > 0 ? `Document handling fee: $${documentHandlingFee(jobInput.pageCount).toFixed(2)}` : 'Within the free page allowance.',
                at: 'Just now (synthetic)',
              },
            ]
          : []),
        ...(jobInput.witnessFeeAmount
          ? [
              {
                label: `Witness fee recorded: $${jobInput.witnessFeeAmount.toFixed(2)}`,
                detail: `Total charged with $${WITNESS_FEE_ADMIN_SURCHARGE_USD.toFixed(2)} administrative fee: $${witnessFeeTotal(jobInput.witnessFeeAmount).toFixed(2)}.`,
                at: 'Just now (synthetic)',
              },
            ]
          : []),
      ],
      attempt: null,
    };
  });
  const file: CompanyFile = {
    id: fileId,
    caption: input.caption,
    caseNumber: input.caseNumber,
    court: input.court,
    stateCode: input.stateCode,
    originatingCustomer: input.originatingCustomer || undefined,
    sendingCompanyName: input.sendingCompanyName || undefined,
    sourceOrderReference: input.sourceOrderReference || undefined,
    jobIds: newJobs.map((job) => job.id),
  };
  state = {
    ...state,
    files: [...state.files, file],
    jobs: {...state.jobs, ...Object.fromEntries(newJobs.map((job) => [job.id, job]))},
  };
  emit();
  return fileId;
}

let platformSequence = 100;

function createPlatformIntakeRequestSync(input: NewCompanyFileInput, idempotencyKey: string): string {
  seenIdempotencyKeys.add(idempotencyKey);
  platformSequence += 1;
  const requestId = `PF-INTAKE-${String(platformSequence).padStart(5, '0')}`;
  const request: PlatformIntakeRequest = {
    id: requestId,
    caption: input.caption,
    caseNumber: input.caseNumber,
    court: input.court,
    stateCode: input.stateCode,
    originatingCustomer: input.originatingCustomer || undefined,
    sendingCompanyName: input.sendingCompanyName || undefined,
    sourceOrderReference: input.sourceOrderReference || undefined,
    jobs: input.jobs,
    submittedAt: 'Just now (synthetic)',
    version: 1,
  };
  state = {...state, platformIntakeRequests: [...state.platformIntakeRequests, request]};
  emit();
  return requestId;
}

function routePlatformIntakeToCompanySync(requestId: string, expectedVersion: number, idempotencyKey: string): string {
  const request = state.platformIntakeRequests.find((item) => item.id === requestId);
  if (!request) throw new Error(`Unknown platform intake request: ${requestId}`);
  if (request.version !== expectedVersion) {
    throw new WorkflowConflictError(`${requestId} changed since it was loaded (expected version ${expectedVersion}, currently ${request.version}). Reload before retrying.`);
  }
  const fileId = createCompanyFileSync(
    {
      caption: request.caption,
      caseNumber: request.caseNumber,
      court: request.court,
      stateCode: request.stateCode,
      originatingCustomer: request.originatingCustomer,
      sendingCompanyName: request.sendingCompanyName,
      sourceOrderReference: request.sourceOrderReference,
      jobs: request.jobs,
    },
    idempotencyKey,
  );
  state = {...state, platformIntakeRequests: state.platformIntakeRequests.filter((item) => item.id !== requestId)};
  emit();
  return fileId;
}

/**
 * The exact contract every screen in this lane depends on -- named
 * explicitly (per the founder's "backend-ready UI adapters" directive) so a
 * real, backend-wired implementation can be type-checked against it and
 * swapped in without touching a single screen. Every method already takes
 * an `expectedVersion` + `idempotencyKey` and returns a `Promise`, matching
 * the shape a real fetch-based implementation naturally has -- this
 * interface just gives that shape a name and a place to check conformance,
 * it does not change any behavior below.
 *
 * A real implementation satisfying this type is a drop-in replacement:
 * `subscribe`/`getSnapshot` stay in place for React's `useSyncExternalStore`
 * (they'd wrap a fetch-and-cache instead of an in-memory object), and every
 * mutation keeps its signature -- callers never know the difference. `reset()`
 * is the one method with no real-backend equivalent; it exists for tests
 * and demos only and should not be part of a real adapter's public surface.
 */
export interface CarolinaWorkflowAdapter {
  subscribe(listener: () => void): () => void;
  getSnapshot(): WorkflowState;
  acknowledgePackage(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob>;
  submitAttempt(jobId: string, expectedVersion: number, idempotencyKey: string, attempt: {result: ResultPath; narrative: string; evidenceFileName?: string; evidenceDataUrl?: string}): Promise<ServiceJob>;
  acceptAttempt(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob>;
  returnForCorrection(jobId: string, expectedVersion: number, idempotencyKey: string, correctionNote: string): Promise<ServiceJob>;
  assignServer(jobId: string, expectedVersion: number, idempotencyKey: string, server: string): Promise<ServiceJob>;
  declineAssignment(jobId: string, expectedVersion: number, idempotencyKey: string, reason: string): Promise<ServiceJob>;
  startAffidavitPrep(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob>;
  submitAffidavitForReview(jobId: string, expectedVersion: number, idempotencyKey: string, affidavit: {narrative: string; path: AttestationPath; notaryName?: string; militaryStatusStatement?: string}): Promise<ServiceJob>;
  acceptAffidavit(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob>;
  returnAffidavitForCorrection(jobId: string, expectedVersion: number, idempotencyKey: string, correctionNote: string): Promise<ServiceJob>;
  closeJob(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob>;
  closeFinancialPeriod(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob>;
  createCompanyFile(input: NewCompanyFileInput, idempotencyKey: string): Promise<string>;
  createPlatformIntakeRequest(input: NewCompanyFileInput, idempotencyKey: string): Promise<string>;
  routePlatformIntakeToCompany(requestId: string, expectedVersion: number, idempotencyKey: string): Promise<string>;
  /** Synthetic-only: resets in-memory fixture state for tests/demos. A real adapter has no equivalent and should not implement this. */
  reset(): void;
}

export const carolinaWorkflowStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot(): WorkflowState {
    return state;
  },

  acknowledgePackage(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob> {
    return mutate(() => setJob(jobId, expectedVersion, idempotencyKey, {packageState: 'acknowledged'}, {label: 'Package acknowledged by server'}));
  },

  submitAttempt(
    jobId: string,
    expectedVersion: number,
    idempotencyKey: string,
    attempt: {result: ResultPath; narrative: string; evidenceFileName?: string; evidenceDataUrl?: string},
  ): Promise<ServiceJob> {
    return mutate(() => {
      const job = requireJob(jobId);
      const wasCorrection = job.attempt?.reviewState === 'returned_for_correction';
      const history =
        wasCorrection && job.attempt
          ? [
              ...(job.attempt.history ?? []),
              {
                result: job.attempt.result,
                narrative: job.attempt.narrative,
                evidenceFileName: job.attempt.evidenceFileName,
                correctionNote: job.attempt.correctionNote ?? '',
              },
            ]
          : job.attempt?.history;
      return setJob(
        jobId,
        expectedVersion,
        idempotencyKey,
        {
          attempt: {
            result: attempt.result,
            narrative: attempt.narrative,
            evidenceFileName: attempt.evidenceFileName,
            evidenceDataUrl: attempt.evidenceDataUrl,
            attemptedAt: 'Just now (synthetic)',
            reviewState: 'pending_review',
            history,
          },
        },
        {label: wasCorrection ? 'Corrected attempt submitted' : 'Attempt submitted', detail: attempt.narrative},
      );
    });
  },

  acceptAttempt(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob> {
    return mutate(() => {
      const job = requireJob(jobId);
      if (!job.attempt) throw new Error(`${jobId} has no attempt to accept.`);
      return setJob(
        jobId,
        expectedVersion,
        idempotencyKey,
        {attempt: {...job.attempt, reviewState: 'accepted', correctionNote: undefined}},
        {label: 'Attempt accepted'},
      );
    });
  },

  returnForCorrection(jobId: string, expectedVersion: number, idempotencyKey: string, correctionNote: string): Promise<ServiceJob> {
    return mutate(() => {
      const job = requireJob(jobId);
      if (!job.attempt) throw new Error(`${jobId} has no attempt to return for correction.`);
      return setJob(
        jobId,
        expectedVersion,
        idempotencyKey,
        {attempt: {...job.attempt, reviewState: 'returned_for_correction', correctionNote}},
        {label: 'Attempt returned for correction', detail: correctionNote},
      );
    });
  },

  assignServer(jobId: string, expectedVersion: number, idempotencyKey: string, server: string): Promise<ServiceJob> {
    return mutate(() => {
      if (server === 'Unassigned') throw new Error('Choose a server to assign.');
      return setJob(jobId, expectedVersion, idempotencyKey, {server, episodeId: 'Assignment episode 1'}, {label: `Assigned to ${server}`});
    });
  },

  /**
   * A server can decline an assignment before acknowledging the package
   * (once acknowledged, they're committed and any handoff goes through the
   * company, not a self-service decline). Returns the job to Unassigned so
   * Mission Control sees it back in the "needs a server" queue immediately.
   */
  declineAssignment(jobId: string, expectedVersion: number, idempotencyKey: string, reason: string): Promise<ServiceJob> {
    return mutate(() => {
      const job = requireJob(jobId);
      if (job.packageState !== 'awaiting_acknowledgment') {
        throw new Error(`${jobId} has already been acknowledged and can't be self-declined. Contact the company to reassign it.`);
      }
      const declinedBy = job.server;
      return setJob(
        jobId,
        expectedVersion,
        idempotencyKey,
        {server: 'Unassigned', episodeId: ''},
        {label: `Declined by ${declinedBy}`, detail: reason},
      );
    });
  },

  startAffidavitPrep(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob> {
    return mutate(() => {
      const job = requireJob(jobId);
      if (job.attempt?.reviewState !== 'accepted') throw new Error(`${jobId}'s attempt must be accepted before affidavit prep can start.`);
      if (job.affidavitState !== 'not_started') throw new Error(`${jobId}'s affidavit has already moved past not_started.`);
      return setJob(jobId, expectedVersion, idempotencyKey, {affidavitState: 'server_preparing'}, {label: 'Affidavit preparation started'});
    });
  },

  submitAffidavitForReview(
    jobId: string,
    expectedVersion: number,
    idempotencyKey: string,
    attestation: {narrative: string; path: AttestationPath; notaryName?: string; militaryStatusStatement?: string},
  ): Promise<ServiceJob> {
    return mutate(() => {
      const job = requireJob(jobId);
      if (job.affidavitState !== 'server_preparing') throw new Error(`${jobId}'s affidavit is not awaiting server preparation.`);
      const jurisdictionRule = getAffidavitJurisdictionRule(fileForJob(job)?.stateCode);
      if (jurisdictionRule?.militaryStatusRequired && !attestation.militaryStatusStatement?.trim()) {
        throw new Error(`${jurisdictionRule.stateName} requires a military-status declaration in this affidavit. Add one before submitting for review.`);
      }
      return setJob(
        jobId,
        expectedVersion,
        idempotencyKey,
        {
          affidavitState: 'company_review',
          affidavitNarrative: attestation.narrative,
          affidavitPath: attestation.path,
          affidavitNotaryName: attestation.notaryName,
          affidavitMilitaryStatusStatement: attestation.militaryStatusStatement,
          affidavitCorrectionNote: undefined,
        },
        {label: 'Affidavit submitted for company review'},
      );
    });
  },

  acceptAffidavit(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob> {
    return mutate(() => {
      const job = requireJob(jobId);
      if (job.affidavitState !== 'company_review') throw new Error(`${jobId}'s affidavit is not awaiting company review.`);
      return setJob(jobId, expectedVersion, idempotencyKey, {affidavitState: 'accepted'}, {label: 'Affidavit accepted'});
    });
  },

  returnAffidavitForCorrection(jobId: string, expectedVersion: number, idempotencyKey: string, correctionNote: string): Promise<ServiceJob> {
    return mutate(() => {
      const job = requireJob(jobId);
      if (job.affidavitState !== 'company_review') throw new Error(`${jobId}'s affidavit is not awaiting company review.`);
      return setJob(
        jobId,
        expectedVersion,
        idempotencyKey,
        {affidavitState: 'server_preparing', affidavitCorrectionNote: correctionNote},
        {label: 'Affidavit returned for correction', detail: correctionNote},
      );
    });
  },

  closeJob(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob> {
    return mutate(() => {
      const job = requireJob(jobId);
      if (job.affidavitState !== 'accepted') throw new Error(`${jobId} needs an accepted affidavit before operational closure.`);
      return setJob(jobId, expectedVersion, idempotencyKey, {operationalClosure: 'complete'}, {label: 'Marked operationally complete'});
    });
  },

  /**
   * Found missing during the 2026-09-11 full end-to-end UAT walkthrough: a
   * freshly-created job could reach operational closure but had no path to
   * financial closure at all -- no mutation set it, ever, for a job created
   * through the live flow (only pre-seeded fixture data started 'closed').
   * That silently blocked the NPS/feedback flow, whose eligibility gate
   * requires both (see JobCompletionFeedback.tsx). Deliberately requires
   * operational closure first, matching the real invoice-after-completion
   * sequence the founder's model describes.
   */
  closeFinancialPeriod(jobId: string, expectedVersion: number, idempotencyKey: string): Promise<ServiceJob> {
    return mutate(() => {
      const job = requireJob(jobId);
      if (job.operationalClosure !== 'complete') throw new Error(`${jobId} needs operational closure before the invoice can be marked paid.`);
      return setJob(jobId, expectedVersion, idempotencyKey, {financialClosure: 'closed'}, {label: 'Invoice marked paid'});
    });
  },

  createCompanyFile(input: NewCompanyFileInput, idempotencyKey: string): Promise<string> {
    return mutate(() => createCompanyFileSync(input, idempotencyKey));
  },

  /**
   * Pattern A ("platform-routed", the recommended default -- matches the
   * real system's loadIntake -> claimMissionControlCustody ->
   * createFulfillmentRoute shape): the request lands in a platform-level
   * queue, not any company's Mission Control, until explicitly routed.
   */
  createPlatformIntakeRequest(input: NewCompanyFileInput, idempotencyKey: string): Promise<string> {
    return mutate(() => createPlatformIntakeRequestSync(input, idempotencyKey));
  },

  routePlatformIntakeToCompany(requestId: string, expectedVersion: number, idempotencyKey: string): Promise<string> {
    return mutate(() => routePlatformIntakeToCompanySync(requestId, expectedVersion, idempotencyKey));
  },

  reset() {
    state = initialState();
    sequence = 100;
    platformSequence = 100;
    seenIdempotencyKeys.clear();
    holmesThinkingStore.reset();
    emit();
  },
} satisfies CarolinaWorkflowAdapter;

export function useCarolinaWorkflow(): WorkflowState {
  return useSyncExternalStore(carolinaWorkflowStore.subscribe, carolinaWorkflowStore.getSnapshot);
}

export function jobsForFile(state: WorkflowState, fileId: string): ServiceJob[] {
  const file = state.files.find((item) => item.id === fileId);
  if (!file) return [];
  return file.jobIds.map((jobId) => state.jobs[jobId]).filter((job): job is ServiceJob => Boolean(job));
}

export function jobsForServer(state: WorkflowState, server: string): ServiceJob[] {
  return Object.values(state.jobs)
    .filter((job) => job.server === server)
    .sort((a, b) => a.deadlineSortKey.localeCompare(b.deadlineSortKey));
}
